import { Component, OnInit } from '@angular/core';
import { AlertController, LoadingController } from '@ionic/angular';
import _ from 'lodash';

import { FEATURES } from 'src/app/constants';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

@Component({
  selector: 'app-admin-security',
  templateUrl: './admin-security.component.html',
  styleUrls: ['./admin-security.component.scss'],
})
export class AdminSecurityComponent implements OnInit {
  featureEnabled = true;
  allowedUserEmailDomains: string[] = [];
  emailDomains: any[] = [];
  excludeDomainFromHost = false;
  showValidateEmailDomainModal = false;
  dnsValidation: any[] = [];
  emailDomainToValidate: any;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private apiService: ApiService,
  ) {}

  async ngOnInit() {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.INBOUND_EMAIL);
    this.allowedUserEmailDomains = this.authService.tenant?.allowedEmailDomains;

    await Promise.all([this.loadEmailDomains()]);
  }

  copyValidationToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async loadEmailDomains() {
    const emailDomainsResponse: any = await this.apiService.postPromise('/email-domains/list', {
      limit: null,
    });

    this.emailDomains = emailDomainsResponse.data;
  }

  async addEmailDomain() {
    const confirm = await this.alertCtrl.create({
      header: `Enter Email Domain`,
      subHeader: `Only enter a domain name, e.g. "unthread.io", not a full email address`,
      cssClass: 'wide-alert-controller',
      inputs: [
        {
          placeholder: 'Enter a domain',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Submit',
          handler: async (data: any[]) => {
            confirm.dismiss();

            let domain = data[0];

            if (!domain) {
              this.msgSrvc.showError('Please enter a domain');
              return false;
            }

            if (domain.includes('@')) {
              this.msgSrvc.showError('Please enter a domain, not a full email address');
              return false;
            }

            const loader = await this.loadingCtrl.create({
              message: 'Submitting...',
              spinner: 'crescent',
            });

            loader.present();

            try {
              const emailDomain = await this.apiService.postPromise('/email-domains', {
                domain: domain.toLowerCase().trim(),
              });

              this.validateEmailDomain(emailDomain);

              await this.loadEmailDomains();
              loader.dismiss();
            } catch (e: any) {
              loader.dismiss();
              this.msgSrvc.showError(e);
            }
          },
        },
      ],
    });

    confirm.present();
  }

  async validateEmailDomain(emailDomain: any) {
    this.showValidateEmailDomainModal = true;
    this.emailDomainToValidate = emailDomain;

    this.dnsValidation = [emailDomain.dns.mail_cname, emailDomain.dns.dkim1, emailDomain.dns.dkim2];
  }

  getHostDisplayValue(host: string) {
    return this.excludeDomainFromHost ? host.replace(`.${this.emailDomainToValidate.domain}`, '') : host;
  }

  onDismissValidateEmailDomainModal() {
    this.showValidateEmailDomainModal = false;
  }

  async submitEmailDomainValidation() {
    const loader = await this.loadingCtrl.create({
      message: 'Checking...',
      spinner: 'crescent',
    });

    loader.present();

    try {
      const result: any = await this.apiService.postPromise(
        `/email-domains/${this.emailDomainToValidate.id}/validate`,
        {},
      );

      await this.loadEmailDomains();

      if (result.valid) {
        this.onDismissValidateEmailDomainModal();
      }
    } catch (e: any) {
      this.msgSrvc.showError(e);
    } finally {
      loader.dismiss();
    }
  }

  async deleteEmailDomain(emailDomainId: string) {
    const loader = await this.loadingCtrl.create({
      message: 'Deleting...',
      spinner: 'crescent',
    });

    loader.present();

    try {
      await this.apiService.deletePromise(`/email-domains/${emailDomainId}`);

      await this.loadEmailDomains();
    } catch (e: any) {
      this.msgSrvc.showError(e);
    } finally {
      loader.dismiss();
    }
  }

  async addUserEmailDomain() {
    const confirm = await this.alertCtrl.create({
      header: `Enter Email Domain`,
      subHeader: `Only enter a domain name, e.g. "unthread.io". Adding a domain allows anyone with that email domain to access your dashboard and will affect who is considered a customer or an agent.`,
      inputs: [
        {
          placeholder: 'Enter a domain',
        },
      ],
      buttons: [
        {
          text: 'Cancel',
          handler: () => {},
        },
        {
          text: 'Submit',
          handler: async (data: any[]) => {
            let domain = data[0];

            if (!domain) {
              this.msgSrvc.showError('Please enter a domain');
              return false;
            }

            this.allowedUserEmailDomains.push(domain);

            try {
              await this.authService.updateTenant({
                allowedEmailDomains: [...this.allowedUserEmailDomains],
              });

              this.msgSrvc.showBottom('✔︎ Saved');
            } catch (err) {
              this.msgSrvc.showBottom(`Whoops! An error occurred: ${err.message}`);
            }
          },
        },
      ],
    });

    confirm.present();
  }

  async deleteUserDomain(index: number) {
    const alert = await this.alertCtrl.create({
      header: 'Remove this domain?',
      cssClass: 'admin-settings-warning-modal',
      subHeader:
        'Removing allowed email domains changes who will be considered a member of your workspace. Adding a domain allows anyone with that email domain to access your dashboard and will affect who is considered a customer or an agent. Are you sure you wish to proceed?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            this.allowedUserEmailDomains.splice(index, 1);

            try {
              await this.authService.updateTenant({
                allowedEmailDomains: [...this.allowedUserEmailDomains],
              });

              this.msgSrvc.showBottom('✔︎ Saved');
            } catch (err) {
              this.msgSrvc.showBottom(`Whoops! An error occurred: ${err.message}`);
            }
          },
        },
      ],
    });

    await alert.present();
  }
}
